import Papa from 'papaparse';
import type { Curve, ParsedData } from '../../../interfaces/toolData';

export class VertexPulserParser {
  parse(content: string, _filename: string): ParsedData {
    try {
      if (!content || content.trim() === '') {
        throw new Error('File is empty');
      }

      // Parse the entire CSV content with relaxed error handling
      const parseResult = Papa.parse(content, {
        header: true,
        skipEmptyLines: 'greedy', // More aggressive empty line skipping
        transformHeader: (header: string) => header.trim(), // Clean up header spaces
        dynamicTyping: false // Keep everything as strings
      });

      // Suppress CSV parsing warnings for now - this format has inconsistent field counts
      if (parseResult.errors.length > 100) {
        console.warn(`CSV parsing: ${parseResult.errors.length} field mismatch warnings (suppressed)`);
      }

      const rawData = parseResult.data as Record<string, string>[];
      
      // Extract metadata from info rows
      const metadata = this.parseMetadata(rawData);
      
      // Group measurement events by type and create time series
      const curves = this.createTimeSeriesFromEvents(rawData);
      
      // Create data preview - show rows that have any non-empty data values
      const dataRows = rawData.filter(row => {
        const timestampValue = row['Date/Time'];
        const hasTimestamp = timestampValue && timestampValue.trim();
        const hasData = Object.keys(row).some(col => {
          const value = row[col];
          return col !== 'Date/Time' && 
                 col !== 'Log Start' && 
                 col !== 'Info' && 
                 col !== 'Category' &&
                 !col.startsWith('__') && // Skip Papa.parse internal fields
                 value && typeof value === 'string' && value.trim() !== '';
        });
        return hasTimestamp && hasData;
      });
      
      const dataPreview = {
        totalCount: dataRows.length,
        first5: dataRows.slice(0, 5),
        last5: dataRows.slice(-5)
      };

      return {
        toolType: 'Vertex',
        fileType: 'VERTEX_PULSER_DEFAULT',
        curves,
        metadata,
        dataPreview,
        errors: []
      };

    } catch (error) {
      throw new Error(`Failed to parse Vertex pulser file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Parse metadata from info and system message rows
   */
  private parseMetadata(rows: Record<string, string>[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    for (const row of rows) {
      const info = row['Info']?.trim();
      
      if (!info) continue;
      
      // Extract firmware version
      const firmwareMatch = info.match(/Vertex Pulser Firmware Version ([\d.]+)/);
      if (firmwareMatch) {
        metadata.firmwareVersion = firmwareMatch[1];
      }
      
      // Extract serial numbers
      const barrelSerialMatch = info.match(/Barrel Serial No: (.+)/);
      if (barrelSerialMatch) {
        metadata.barrelSerialNo = barrelSerialMatch[1];
      }
      
      const pcbSerialMatch = info.match(/PCB Serial No: (.+)/);
      if (pcbSerialMatch) {
        metadata.pcbSerialNo = pcbSerialMatch[1];
      }
    }
    
    return metadata;
  }



  /**
   * Create time series curves from measurement events
   */
  private createTimeSeriesFromEvents(rows: Record<string, string>[]): Curve[] {
    const curves: Curve[] = [];
    
    if (rows.length === 0) return curves;
    
    // Get all column names except the ones we want to skip
    const firstRow = rows[0];
    const dataColumns = Object.keys(firstRow).filter(col => 
      col !== 'Date/Time' && 
      col !== 'Log Start' && 
      col !== 'Info' && 
      col !== 'Category' &&
      col !== 'RPM Dev' && // Ignore RPM Dev for now
      !col.startsWith('__') // Skip Papa.parse internal fields like __parsed_extra
    );
    
    // Create a curve for each data column that has values
    for (const columnName of dataColumns) {
      const dataPoints: Array<{ timestamp: Date, value: number }> = [];
      
      for (const row of rows) {
        const timestampValue = row['Date/Time'];
        const columnValue = row[columnName];
        
        if (!timestampValue || !columnValue) {
          continue;
        }
        
        const timestampStr = timestampValue.trim();
        const valueStr = columnValue.trim();
        
        if (!timestampStr || !valueStr || valueStr === '') {
          continue;
        }
        
        const numericValue = this.parseNumericValue(valueStr, columnName);
        if (numericValue === null) {
          continue;
        }
        
        try {
          const timestamp = this.parseVertexTimestamp(timestampStr);
          dataPoints.push({ timestamp, value: numericValue });
        } catch (error) {
          // Skip invalid timestamps
          continue;
        }
      }
      
      // Only create a curve if we have data points
      if (dataPoints.length > 0) {
        // Sort by timestamp
        dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        
        const unit = this.getUnitForColumnName(columnName);
        
        curves.push({
          name: columnName,
          xValues: dataPoints.map(p => p.timestamp),
          yValues: dataPoints.map(p => p.value),
          yUnit: unit
        });
      }
    }
    
    return curves;
  }



  /**
   * Parse a numeric value from a string, handling empty/null values and special text conversions
   */
  private parseNumericValue(value: string | undefined, columnName?: string): number | null {
    if (!value || value.trim() === '') {
      return null;
    }
    
    const trimmedValue = value.trim();
    
    // Handle special text-to-numeric conversions based on column name
    if (columnName === 'Flow Switch') {
      if (trimmedValue === 'ON') return 1;
      if (trimmedValue === 'OFF') return 0;
    }
    
    if (columnName === 'RPM Rotate') {
      if (trimmedValue === 'DL Rotate Start') return 1;
      if (trimmedValue === 'DL Rotate Stop' || trimmedValue === 'Stop') return 0;
    }
    
    // Try to parse as regular number
    const parsed = Number(trimmedValue);
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Get the appropriate unit for a column name
   */
  private getUnitForColumnName(columnName: string): string {
    if (columnName.includes('ms')) {
      return 'ms';
    }
    if (columnName.includes('Voltage')) {
      return 'V';
    }
    if (columnName.includes('Current')) {
      return 'A';
    }
    if (columnName.includes('Temperature')) {
      return '°C';
    }
    if (columnName.includes('RPM')) {
      return 'RPM';
    }
    if (columnName.includes('Flow')) {
      return 'units'; // Could be mL/min or similar
    }
    if (columnName.includes('Accel')) {
      return 'g'; // Acceleration units
    }
    if (columnName.includes('Poppet')) {
      return 'mils'; // Position units
    }
    if (columnName === 'Flow Switch' || columnName === 'RPM Rotate') {
      return 'boolean'; // Binary on/off values
    }
    return 'units';
  }

  /**
   * Parse Vertex timestamp format: + 0:00:08:02.224
   */
  private parseVertexTimestamp(timestamp: string): Date {
    const match = timestamp.match(/^\+\s*(\d+):(\d+):(\d+):(\d+)\.(\d+)$/);
    if (!match) {
      throw new Error(`Invalid Vertex timestamp format: ${timestamp}`);
    }

    const [, days, hours, minutes, seconds, milliseconds] = match;
    
    // Convert to total milliseconds from epoch
    const totalMs = 
      parseInt(days) * 24 * 60 * 60 * 1000 +
      parseInt(hours) * 60 * 60 * 1000 +
      parseInt(minutes) * 60 * 1000 +
      parseInt(seconds) * 1000 +
      parseInt(milliseconds);

    return new Date(totalMs);
  }
}

